"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BoundedReadAdmission = exports.ReadAdmissionError = void 0;
class ReadAdmissionError extends Error {
    constructor(reason) {
        super(`Read admission: ${reason}`);
        this.reason = reason;
    }
}
exports.ReadAdmissionError = ReadAdmissionError;
/** Shared in-process admission, not an authorization cache or a job queue.
 * FIFO within a viewer (or a key without viewers), round-robin across viewers and keys, so a backlog of one key cannot
 * hold the instance capacity other keys wait for; bounds both active work and
 * queued requests.
 * A deadline ends the response but capacity belongs to the actual task until
 * it settles, including readers which cannot cancel their database operation.
 */
class BoundedReadAdmission {
    constructor(limits) {
        this.limits = limits;
        this.states = new Map();
        /** Keys with waiters, least recently served first. */
        this.rotation = [];
        this.waiting = 0;
        this.active = 0;
    }
    async run(key, task, options = {}) {
        const waitingAt = performance.now();
        let state;
        try {
            state = await this.acquire(key, options.viewerKey ?? null, options.signal);
        }
        finally {
            options.onWait?.(performance.now() - waitingAt);
        }
        const abort = new AbortController();
        const startedAt = performance.now();
        let timer;
        let cancel = () => { };
        const deadline = new Promise((_resolve, reject) => {
            const fail = (reason) => {
                const error = new ReadAdmissionError(reason);
                abort.abort(error);
                reject(error);
            };
            cancel = () => fail('cancelled');
            options.signal?.addEventListener('abort', cancel, { once: true });
            timer = setTimeout(() => fail('read_timeout'), this.limits.readTimeoutMs);
            if (options.signal?.aborted)
                cancel();
        });
        const read = Promise.resolve()
            .then(() => {
            abort.signal.throwIfAborted();
            return task(abort.signal);
        })
            .finally(() => {
            clearTimeout(timer);
            options.signal?.removeEventListener('abort', cancel);
            if (options.viewerKey != null) {
                const remaining = state.viewers.get(options.viewerKey) - 1;
                if (remaining)
                    state.viewers.set(options.viewerKey, remaining);
                else
                    state.viewers.delete(options.viewerKey);
            }
            state.active -= 1;
            this.active -= 1;
            this.prune(key, state);
            this.drain();
            options.onRead?.(performance.now() - startedAt);
        });
        try {
            return await Promise.race([read, deadline]);
        }
        finally {
            clearTimeout(timer);
            options.signal?.removeEventListener('abort', cancel);
        }
    }
    available(state, viewerKey) {
        return (state.active < this.limits.concurrency &&
            (viewerKey === null ||
                (state.viewers.get(viewerKey) ?? 0) <
                    Math.max(1, Math.floor(this.limits.concurrency / 2))) &&
            this.active < (this.limits.totalConcurrency?.() ?? Infinity));
    }
    acquire(key, viewerKey, signal) {
        if (signal?.aborted)
            return Promise.reject(new ReadAdmissionError('cancelled'));
        const state = this.states.get(key) ?? {
            active: 0,
            viewers: new Map(),
            waiters: [],
        };
        this.states.set(key, state);
        this.drain();
        if (this.available(state, viewerKey) &&
            !state.waiters.some(waiter => waiter.viewerKey === viewerKey)) {
            this.start(state, viewerKey);
            return Promise.resolve(state);
        }
        if (state.waiters.length >= this.limits.maxWaiting ||
            (viewerKey !== null &&
                state.waiters.filter(waiter => waiter.viewerKey === viewerKey).length >=
                    Math.max(1, Math.floor(this.limits.maxWaiting / 2))) ||
            this.waiting >= (this.limits.totalMaxWaiting ?? Infinity)) {
            this.prune(key, state);
            return Promise.reject(new ReadAdmissionError('queue_full'));
        }
        return new Promise((resolve, reject) => {
            const remove = () => {
                clearTimeout(timer);
                signal?.removeEventListener('abort', cancel);
                const index = state.waiters.indexOf(waiter);
                if (index < 0)
                    return;
                state.waiters.splice(index, 1);
                this.waiting -= 1;
                if (!state.waiters.length)
                    this.rotation.splice(this.rotation.indexOf(key), 1);
            };
            const waiter = {
                viewerKey,
                start: () => {
                    remove();
                    this.start(state, viewerKey);
                    resolve(state);
                },
                cancel: reason => {
                    remove();
                    this.prune(key, state);
                    reject(new ReadAdmissionError(reason));
                },
            };
            const cancel = () => waiter.cancel('cancelled');
            const timer = setTimeout(() => waiter.cancel('wait_timeout'), this.limits.waitTimeoutMs);
            if (!state.waiters.length)
                this.rotation.push(key);
            state.waiters.push(waiter);
            this.waiting += 1;
            signal?.addEventListener('abort', cancel, { once: true });
        });
    }
    drain() {
        // Each start moves its key behind every other waiting key; keys already
        // using their own concurrency allowance are skipped, not blocking others.
        for (let index = 0; index < this.rotation.length;) {
            const key = this.rotation[index];
            const state = this.states.get(key);
            // Include immediately admitted work in the viewer rotation as well.
            const previous = state.lastViewer;
            state.waiters = [
                ...state.waiters.filter(waiter => waiter.viewerKey !== previous),
                ...state.waiters.filter(waiter => waiter.viewerKey === previous),
            ];
            const waiter = state.waiters.find(waiter => this.available(state, waiter.viewerKey));
            if (!waiter) {
                index += 1;
                continue;
            }
            waiter.start();
            if (state.waiters.length) {
                this.rotation.splice(index, 1);
                this.rotation.push(key);
            }
        }
    }
    start(state, viewerKey) {
        state.lastViewer = viewerKey;
        state.active += 1;
        this.active += 1;
        if (viewerKey !== null) {
            state.viewers.set(viewerKey, (state.viewers.get(viewerKey) ?? 0) + 1);
        }
    }
    prune(key, state) {
        if (!state.active && !state.waiters.length)
            this.states.delete(key);
    }
}
exports.BoundedReadAdmission = BoundedReadAdmission;
